import { useState, type ReactNode } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Check,
  CheckCircle2,
  Ban,
  CalendarClock,
  CalendarPlus,
  ChevronDown,
  ChevronLeft,
  Download,
  Edit,
  Euro,
  FilePlus,
  FileText,
  Navigation,
  Pencil,
  PencilLine,
  Phone,
  Play,
  Plus,
  ReceiptText,
  RotateCcw,
  Send,
  Trash2,
  User,
  UserPlus,
  UserX,
  X,
} from 'lucide-react'
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon'
import mapPinIcon from '@/assets/jobs/detail/map-pin.svg'
import arrowRightIcon from '@/assets/jobs/detail/arrow-right.svg'
import userIcon from '@/assets/jobs/detail/user.svg'
import noteEditIcon from '@/assets/jobs/detail/note-edit.svg'
import noteTrashIcon from '@/assets/jobs/detail/note-trash.svg'
import { Button } from '@/components/ui/button'
import { Permit } from '@/components/guards/Permit'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { FORM_TEXTAREA_CLASS } from '@/components/ui/form-control-styles'
import { Badge } from '@/components/ui/badge'
import { Avatar } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/toast'
import { CardHeadingBar } from '@/components/shared/CardHeadingBar'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { LoadingOverlay } from '@/components/shared'
import {
  JobPriorityBadge,
  JobStatusBadge,
  ProspectBadge,
} from '@/components/shared/StatusBadges'
import { JobAttachments } from '@/components/jobs/JobAttachments'
import { JobReportView } from '@/components/jobs/JobReportView'
import { SendReportDialog } from '@/components/jobs/SendReportDialog'
import { ChaseInvoiceCard } from '@/components/jobs/ChaseInvoiceCard'
import {
  PaymentStatusBadge,
  QuoteStatusBadge,
} from '@/components/shared/StatusBadges'
import { PAYMENT_METHOD_LABELS } from '@/types'
import {
  CancelJobDialog,
  DeleteJobDialog,
  ReassignDialog,
  StartCloseDialog,
} from '@/components/jobs/JobActionDialogs'
import {
  useCancelJobStartup,
  useCloseJob,
  useJob,
  useJobReport,
  useStartJob,
  useUpdateJob,
} from '@/hooks/useJobs'
import { usePermissions } from '@/hooks/usePermissions'
import { useQuickActions } from '@/hooks/useQuickActions'
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext'
import { jobsApi } from '@/services/jobs'
import { getApiErrorMessage } from '@/services/api'
import { cn } from '@/lib/utils'
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatPostalAddress,
} from '@/lib/format'
import {
  getWarrantyReturnPrimaryActionPath,
  getWarrantyReturnStopPath,
  isWarrantyReturnNothingToPay,
} from '@/lib/jobFollowUpFlow'
import { getJobCollectionBalance } from '@/lib/jobCollectionBalance'
import { getDynamicStopPath } from '@/lib/jobStopFlow'
import { getTechnicianRolePreset } from '@/lib/technicians'
import { primaryEmail, primaryPhone } from '@/lib/clientContacts'
import {
  INTERVENTION_TYPE,
  JOB_STATUS,
  PAYMENT_OPTION,
  PAYMENT_OPTION_LABELS,
  QUOTE_STATUS,
  UNPAID_INVOICE_STATUSES,
} from '@/types'

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className='flex items-center justify-between gap-4 py-2 text-sm'>
      <span className='shrink-0 text-muted-foreground'>{label}</span>
      <span className='flex justify-end text-right font-medium'>
        {value ?? '-'}
      </span>
    </div>
  )
}

function ClientInformationRow({
  label,
  value,
  highlight = false,
}: {
  label: string
  value: React.ReactNode
  highlight?: boolean
}) {
  return (
    <div className='flex items-center gap-2 border-b border-[#e7e9ef] pb-2 text-sm font-normal leading-[1.4]'>
      <span className='w-40 shrink-0 text-[#3d434e]'>{label}</span>
      <span
        className={cn(
          'min-w-0 flex-1 break-words text-right font-medium text-[#171a20]',
          highlight && 'text-[#ff5a1f]'
        )}
      >
        {value || '-'}
      </span>
    </div>
  )
}

function CrSectionHeading({
  title,
  action,
}: {
  title: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className='flex h-12 items-end justify-between gap-3 pr-6'>
      <div className='flex h-[19px] flex-1 items-center border-l-4 border-[#ff5a1f] pl-6 font-heading text-sm font-semibold uppercase leading-[1.2] text-[#6c7285]'>
        {title}
      </div>
      {action}
    </div>
  )
}

type JobPrimaryAction = {
  label: string
  icon: ReactNode
  onClick: () => void | Promise<void>
  disabled?: boolean
}

function CrLightBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className='inline-flex h-6 items-center rounded-lg border border-[#bfdbfe] bg-[#ebf1fe] px-2 text-xs font-medium uppercase leading-4 text-[#2e5ce6]'>
      <span className='whitespace-nowrap'>{children}</span>
    </span>
  )
}

function CrOneTimeTreatmentBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className='inline-flex items-center justify-center rounded-lg border border-[#f5c0ef] bg-[#fcf1fd] px-2 py-1 text-xs font-normal uppercase leading-4 text-[#d732a8]'>
      <span className='whitespace-nowrap'>{children}</span>
    </span>
  )
}

function CrWarrantyReturnBadge() {
  const { t } = useTranslation('jobs')

  return (
    <span className='inline-flex h-6 items-center rounded-lg border border-[#9de100] bg-[#f1f7e3] px-2 text-xs font-medium uppercase leading-4 text-[#518704]'>
      {t('detail.cr.warrantyReturnBadge')}
    </span>
  )
}

function CrUnderContractBadge() {
  const { t } = useTranslation('jobs')

  return (
    <span className='inline-flex h-6 items-center rounded-lg border border-[#fde68a] bg-[#fbf3d9] px-2 text-xs font-medium uppercase leading-4 text-[#ce7e0a]'>
      {t('form.interventionTypes.underContract.label')}
    </span>
  )
}

function CrNoCollectionBillingCard({ text }: { text: string }) {
  const { t } = useTranslation('jobs')

  return (
    <div className='flex min-h-[67px] items-center rounded-xl border border-[#a7f3d0] bg-[#ecfdf5] px-4 py-3'>
      <div className='min-w-0'>
        <div className='text-sm font-normal leading-[1.4] text-[#6c7285]'>
          {t('detail.cr.billing')}
        </div>
        <div className='mt-0.5 truncate font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
          {text}
        </div>
      </div>
    </div>
  )
}

function formatSystemGeneratedJobNote(
  note: string | null | undefined,
  t: (key: string, options?: Record<string, string>) => string
): string {
  const trimmed = note?.trim()
  if (!trimmed) return ''

  const followUpMatch = /^Follow-up proposed from report for (.+)\.$/.exec(
    trimmed
  )
  if (followUpMatch) {
    return t('detail.cr.systemNotes.followUpFromReport', {
      reference: followUpMatch[1],
    })
  }

  const autoBilledMatch = /^Auto-billed from contract (.+)$/.exec(trimmed)
  if (autoBilledMatch) {
    return t('detail.cr.systemNotes.autoBilledFromContract', {
      contract: autoBilledMatch[1],
    })
  }

  return trimmed
}

function CrBillingPaymentRow({
  label,
  amount,
  paid,
  paidLabel,
  statusBadge,
  action,
}: {
  label: React.ReactNode
  amount: number
  paid: boolean
  paidLabel: React.ReactNode
  statusBadge?: React.ReactNode
  action?: {
    label: React.ReactNode
    icon?: React.ReactNode
    onClick: () => void
  } | null
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 rounded-xl border px-4 py-3',
        statusBadge
          ? 'min-h-[79px] border-[#FDE68A] bg-[#FBF3D9]'
          : paid
            ? 'h-[67px] border-[#86efac] bg-[#ecfdf5]'
            : 'h-[67px] border-[#e7e9ef] bg-[#f5f6fa]'
      )}
    >
      <div className='min-w-0'>
        <div className='flex flex-wrap items-center gap-2'>
          <span className='text-sm font-normal leading-[1.4] text-[#6c7285]'>
            {label}
          </span>
          {statusBadge}
        </div>
        <div
          className={cn(
            'truncate font-heading text-xl font-semibold leading-[1.2] text-[#171a20]',
            statusBadge ? 'mt-1' : 'mt-0.5'
          )}
        >
          {formatCurrency(amount, 'EUR', 2)}
        </div>
      </div>

      {paid ? (
        <span className='self-start shrink-0 rounded-lg bg-[#10b981] px-2 py-1 text-xs font-normal leading-none text-white'>
          {paidLabel}
        </span>
      ) : action ? (
        <Permit module='agenda' action='edit'>
          <Button
            className='h-10 shrink-0 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 text-sm font-medium leading-none text-white hover:bg-[#e6501c]'
            onClick={action.onClick}
          >
            {action.icon}
            {action.label}
          </Button>
        </Permit>
      ) : null}
    </div>
  )
}

function formatJobLongDate(
  value: string | null | undefined,
  locale: string
): string {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'

  return new Intl.DateTimeFormat(locale.startsWith('fr') ? 'fr-FR' : 'en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(date)
}

function formatCallbackLongDate(
  value: string | null | undefined,
  locale: string
): string {
  if (!value) return '-'
  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  const date =
    Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)
      ? new Date(year, month - 1, day)
      : new Date(value)
  if (Number.isNaN(date.getTime())) return '-'

  return new Intl.DateTimeFormat(locale.startsWith('fr') ? 'fr-FR' : 'en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date)
}

function callbackTimeWindowTranslationKey(time: string | null): string | null {
  if (time === '08:00') return 'detail.cr.callbackTimeWindows.morning'
  if (time === '14:00') return 'detail.cr.callbackTimeWindows.afternoon'
  return null
}

// Colourful technician avatars mirror the Figma summary card (LC orange, JD
// lime, …). Picked deterministically by index so a given roster stays stable.
const AVATAR_COLORS = [
  '#fa8c16',
  '#a0d911',
  '#3f81ea',
  '#eb2f96',
  '#13c2c2',
  '#722ed1',
]

// Action dialogs this detail view can open (2.3.11-2.3.18).
type JobDialog = 'delete' | 'cancel' | 'close' | 'reassign' | 'send'

interface JobDetailContentProps {
  // Job id to render. In the standalone route this comes from the URL params;
  // in the drawer it is the selected agenda entry.
  id: string
  // 'page' (standalone /jobs/:id route) renders a back arrow that navigates to
  // /calendar. 'drawer' renders an X close affordance that calls onClose so the
  // agenda underneath stays mounted with its week/day state preserved.
  variant?: 'page' | 'drawer'
  // Initial active tab for drawer deep-links from client tables.
  initialTab?: string
  // Close handler for the drawer variant (X / header close button).
  onClose?: () => void
}

// 2.3.6 View job details — quick-action bar, tabs, action buttons.
// Shared body consumed by both the standalone /jobs/:id route (page variant,
// feedback #15 deep-link fallback) and the right-side Agenda drawer (drawer
// variant). The two variants differ only in the header close/back affordance.
export function JobDetailContent({
  id,
  variant = 'page',
  initialTab,
  onClose,
}: JobDetailContentProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation('jobs')
  const { canViewPath, canEdit } = usePermissions()
  const canEditAgenda = canEdit('agenda')
  const canEditInvoices = canEdit('invoices')
  const { callClient, emailClient, navigateToAddress, whatsappClient } =
    useQuickActions()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data, isLoading, isError } = useJob(id)
  const job = data?.data
  useBreadcrumbLabel(t('detail.breadcrumbTitle'))
  const canViewClient =
    !!job?.client_id && canViewPath(`/clients/${job.client_id}`)
  const canViewQuote = (quoteId: string) => canViewPath(`/quotes/${quoteId}`)
  const canViewPayment = (paymentId: string) =>
    canViewPath(`/payments/${paymentId}`)

  const completed = job?.status === JOB_STATUS.completed
  const reportQuery = useJobReport(
    id,
    completed || job?.status === JOB_STATUS.in_progress
  )
  const report = reportQuery.data?.data

  const updateJob = useUpdateJob(id)
  const cancelStartup = useCancelJobStartup(id)
  const startJob = useStartJob(id)
  const closeJob = useCloseJob(id)
  const [noteDraft, setNoteDraft] = useState<string | null>(null)
  const [dialog, setDialog] = useState<JobDialog | null>(null)
  const [exportingReport, setExportingReport] = useState(false)

  const isDrawer = variant === 'drawer'
  const defaultDrawerTab = initialTab ?? 'summary'

  // 'tab' search-param drives the active tab. On the standalone route it stays
  // in the URL; in the drawer we keep it local so parent pages can own their
  // own `?tab=` param without hiding all job tab content.
  const [drawerTab, setDrawerTab] = useState(() =>
    defaultDrawerTab === 'history' ? 'summary' : defaultDrawerTab
  )
  const activeTab = isDrawer
    ? drawerTab
    : (searchParams.get('tab') ?? 'summary')
  const setTab = (tab: string) => {
    if (isDrawer) setDrawerTab(tab)
    else setSearchParams({ tab }, { replace: true })
  }

  const openEditJobForm = () => {
    if (!isDrawer || location.pathname !== '/calendar') {
      navigate(`/calendar?edit_job=${encodeURIComponent(id)}`)
      return
    }

    const next = new URLSearchParams(searchParams)
    next.set('edit_job', id)
    next.delete('job')
    next.delete('tab')
    next.delete('report')
    setSearchParams(next, { replace: false })
  }

  const openRescheduleJobForm = () => {
    if (!isDrawer || location.pathname !== '/calendar') {
      navigate(`/calendar?job=${encodeURIComponent(id)}&reschedule=1`)
      return
    }

    const next = new URLSearchParams(searchParams)
    next.set('job', id)
    next.set('reschedule', '1')
    next.delete('report')
    setSearchParams(next, { replace: false })
  }

  if (isLoading) return <Skeleton className='h-96 w-full' />

  if (isError || !job) {
    return (
      <div className='space-y-4'>
        <p className='text-destructive'>{t('detail.notFound')}</p>
        <Button
          variant='outline'
          onClick={() => (isDrawer ? onClose?.() : navigate('/calendar'))}
        >
          {t('detail.backToAgenda')}
        </Button>
      </div>
    )
  }

  const timeSlot =
    job.start_time && job.end_time ? `${job.start_time} - ${job.end_time}` : '-'
  const isToPlan = job.status === JOB_STATUS.to_plan
  const isWarrantyReturnJob =
    job.intervention_type === INTERVENTION_TYPE.warranty_return
  const isUnderContractJob =
    job.intervention_type === INTERVENTION_TYPE.under_contract
  const amountCollectableIntervention =
    job.intervention_type === INTERVENTION_TYPE.one_time_treatment ||
    isWarrantyReturnJob ||
    isUnderContractJob
  const fullAddress = formatPostalAddress(job)
  const clientPhone = primaryPhone(job.client)
  const clientEmail = primaryEmail(job.client)
  // The refreshed job-detail design is the canonical layout for every job.
  // Intervention type still controls billing/report details, not the layout.
  const isCrExecutionJob = true
  const crHeaderTitle =
    job.reference ||
    job.client?.name ||
    t('detail.jobFallback', { id: job.id.slice(0, 8) })
  const interventionTypeLabel =
    job.intervention_type === INTERVENTION_TYPE.visit_quote
      ? t('form.interventionTypes.visitQuote.label')
      : job.intervention_type === INTERVENTION_TYPE.one_time_treatment
        ? t('form.interventionTypes.oneTimeTreatment.label')
        : job.intervention_type === INTERVENTION_TYPE.warranty_return
          ? t('detail.cr.warrantyReturnBadge')
          : job.intervention_type === INTERVENTION_TYPE.under_contract
            ? t('form.interventionTypes.underContract.label')
            : null
  const interventionTypeBadge =
    amountCollectableIntervention && job.amount_to_collect
      ? `${interventionTypeLabel} ${job.intervention_type === INTERVENTION_TYPE.one_time_treatment ? '•' : '-'} ${formatCurrency(job.amount_to_collect, 'EUR', 2)}`
      : interventionTypeLabel
  const crDateLabel = formatJobLongDate(job.scheduled_date, i18n.language)
  const callbackTimeWindowKey = callbackTimeWindowTranslationKey(
    job.callback_time
  )
  const callbackDescription = job.callback_date
    ? callbackTimeWindowKey
      ? t('detail.cr.callbackDescriptionWithTime', {
          date: formatCallbackLongDate(job.callback_date, i18n.language),
          time: t(callbackTimeWindowKey),
        })
      : t('detail.cr.callbackDescriptionDateOnly', {
          date: formatCallbackLongDate(job.callback_date, i18n.language),
        })
    : null
  const warrantyReturnStopPath = isWarrantyReturnJob
    ? getWarrantyReturnStopPath(job)
    : null
  const warrantyReturnPrimaryActionPath = isWarrantyReturnJob
    ? getWarrantyReturnPrimaryActionPath(job)
    : null
  const warrantyReturnNothingToPay =
    isWarrantyReturnJob && isWarrantyReturnNothingToPay(job)
  const billingAmount = isWarrantyReturnJob
    ? (warrantyReturnStopPath?.amount ?? 0)
    : (job.amount_to_collect ?? 0)
  const hasCollectableAmount = billingAmount > 0
  const underContractNothingToPay = isUnderContractJob && !hasCollectableAmount
  const noteValue = noteDraft ?? job.note ?? ''
  const displayNote = formatSystemGeneratedJobNote(job.note, t)
  const latestNoteActivity = job.status_history?.find(
    item => item.changed_fields && 'note' in item.changed_fields
  )
  const noteModifiedAt =
    latestNoteActivity?.timestamp ??
    latestNoteActivity?.created_at ??
    job.updated_at
  const noteModifiedBy = latestNoteActivity?.user
  const noteModificationLabel = noteModifiedBy
    ? t('detail.noteModifiedBy', {
        date: formatDateTime(noteModifiedAt),
        author: noteModifiedBy,
      })
    : t('detail.noteModifiedOn', { date: formatDateTime(noteModifiedAt) })

  const exportReport = async () => {
    setExportingReport(true)
    try {
      const res = await jobsApi.reportPdf(id)
      if (!res.data?.url) throw new Error(t('shared:exportDialog.error'))
      window.open(res.data.url, '_blank', 'noopener')
      toast({ variant: 'success', title: t('shared:exportDialog.success') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('detail.pdfError')),
      })
    } finally {
      setExportingReport(false)
    }
  }

  const saveNote = async () => {
    const note = noteValue.trim() ? noteValue : ''
    if (!note && !job.note?.trim()) {
      toast({ variant: 'destructive', title: t('detail.noteRequired') })
      return
    }

    try {
      await updateJob.mutateAsync({ note })
      toast({ variant: 'success', title: t('detail.noteSaved') })
    } catch (e) {
      toast({ variant: 'destructive', title: getApiErrorMessage(e) })
    }
  }

  const clearNote = async () => {
    try {
      await updateJob.mutateAsync({ note: '' })
      setNoteDraft('')
      toast({ variant: 'success', title: t('detail.noteSaved') })
    } catch (e) {
      toast({ variant: 'destructive', title: getApiErrorMessage(e) })
    }
  }

  const undoStart = async () => {
    try {
      await cancelStartup.mutateAsync()
      toast({ variant: 'success', title: t('detail.cancelStartupSuccess') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('detail.cancelStartupError')),
      })
    }
  }

  const startIntervention = async () => {
    const hasSchedule = Boolean(
      job.scheduled_date && job.start_time && job.end_time
    )
    const hasTechnician = (job.assigned_technicians ?? []).length > 0

    if (!hasSchedule || !hasTechnician) {
      toast({
        variant: 'destructive',
        title: t('detail.startPrerequisitesRequired'),
      })
      return
    }

    try {
      await startJob.mutateAsync()
      toast({ variant: 'success', title: t('dialogs.startSuccess') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('dialogs.startError')),
      })
    }
  }

  // Per-status action set, matching the Figma job-detail status frames:
  //   planned     → Start · Edit · Delete(icon)
  //   assigned    → Start · Edit · Cancel · Delete(icon)
  //   in_progress → Complete · Cancel
  //   completed   → Create/Edit report
  //   cancelled   → Delete (full-width, destructive)
  const showEdit =
    job.status === JOB_STATUS.to_plan ||
    job.status === JOB_STATUS.planned ||
    job.status === JOB_STATUS.assigned
  const showCancel =
    job.status === JOB_STATUS.assigned || job.status === JOB_STATUS.in_progress
  const showDeleteIcon =
    job.status === JOB_STATUS.to_plan ||
    job.status === JOB_STATUS.planned ||
    job.status === JOB_STATUS.assigned
  const showReschedule =
    job.status === JOB_STATUS.assigned && job.started_at === null
  const showDeleteFull = job.status === JOB_STATUS.cancelled
  const showDuplicate = job.status === JOB_STATUS.cancelled
  const showCreateInvoice =
    canEditInvoices && !job.invoice && job.status !== JOB_STATUS.cancelled
  const reportQuoteId = report?.additional_quote_id ?? null
  const reportQuote = reportQuoteId
    ? (job.quotes?.find(quote => quote.id === reportQuoteId) ?? null)
    : null
  const visitQuoteCandidates =
    job.intervention_type === INTERVENTION_TYPE.visit_quote
      ? (job.quotes ?? []).filter(quote => quote.id !== reportQuoteId)
      : []
  const originalVisitQuote =
    job.intervention_type === INTERVENTION_TYPE.visit_quote
      ? (job.quote_reference ??
        visitQuoteCandidates.find(quote => quote.id === job.quote_id) ??
        visitQuoteCandidates.find(quote => quote.status === QUOTE_STATUS.WON) ??
        visitQuoteCandidates[0])
      : undefined
  const wonVisitQuote =
    originalVisitQuote?.status === QUOTE_STATUS.WON
      ? originalVisitQuote
      : undefined
  const linkedVisitQuote =
    job.intervention_type === INTERVENTION_TYPE.visit_quote
      ? originalVisitQuote
      : undefined
  const hasVisitQuote =
    job.intervention_type === INTERVENTION_TYPE.visit_quote &&
    Boolean(linkedVisitQuote)
  const hasLinkedPayment = Boolean(job.payments?.length)
  const pendingPayment = job.payments?.find(
    payment => payment.status === 'pending'
  )
  const canOpenPendingPayment = pendingPayment
    ? canViewPayment(pendingPayment.id)
    : false
  const pendingPaymentAmount = Math.max(pendingPayment?.amount ?? 0, 0)
  const collectionBalance = getJobCollectionBalance({
    intervention_type: job.intervention_type,
    amount_to_collect: billingAmount > 0 ? billingAmount : pendingPaymentAmount,
    quoteTotal: wonVisitQuote?.total ?? null,
    invoice: job.invoice,
    payments: job.payments,
  })
  const dynamicStopPath = getDynamicStopPath({
    intervention_type: job.intervention_type,
    amount_to_collect: billingAmount,
    quoteTotal: wonVisitQuote?.total ?? null,
    payments: job.payments,
  })
  const paidPaymentAmount = collectionBalance.paidAmount
  const paymentTargetAmount = collectionBalance.targetAmount
  const remainingPaymentAmount = collectionBalance.remainingAmount
  const hasBillingPaid = collectionBalance.isPaid
  const hasPartiallyPaidPayment = collectionBalance.isPartiallyPaid
  const billingStatusBadge = hasPartiallyPaidPayment ? (
    <span className='inline-flex shrink-0 items-center justify-center rounded-lg bg-[#EFAC00] px-2 py-1 text-xs font-normal leading-none text-white'>
      {t('detail.cr.partiallyPaid')}
    </span>
  ) : null

  // Whether a report exists. The report request is the authority once it
  // settles; until then the job detail's report_id answers it, so the action
  // bar does not wait on a second request. Unknown (null) only while the
  // report request is pending and the detail payload carries no report_id.
  const reportExists: boolean | null = reportQuery.isLoading
    ? job.report_id === undefined
      ? null
      : job.report_id !== null
    : !!report

  const fieldWorkClosed = Boolean(job.closed_at)

  const openReportFlow = async () => {
    try {
      if (job.status === JOB_STATUS.in_progress && !fieldWorkClosed) {
        await closeJob.mutateAsync()
      }
      navigate(
        `/calendar?job=${id}&tab=reports&report=${reportExists ? 'edit' : 'create'}`
      )
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('dialogs.closeError')),
      })
    }
  }

  const openCreateReportFromTab = () => {
    if (job.status !== JOB_STATUS.in_progress) {
      toast({
        variant: 'destructive',
        title: t('detail.reportRequiresInProgress'),
      })
      return
    }

    navigate(`/calendar?job=${id}&tab=reports&report=create`)
  }

  const createOrEditReportAction =
    reportExists === null
      ? null
      : {
          label: t('report.headerBadge'),
          icon: <ReceiptText className='mr-2 h-4 w-4' />,
          onClick: openReportFlow,
          disabled: closeJob.isPending,
        }

  const createQuoteForVisit = () => {
    const params = new URLSearchParams({
      client_id: job.client_id,
      job_id: job.id,
      collect_job: job.id,
    })
    navigate(`/quotes/new?${params.toString()}`)
  }

  const openAgendaPayment = (
    amount?: number | null,
    options: { reportAfterPayment?: boolean; invoiceId?: string } = {}
  ) => {
    const params = new URLSearchParams({
      job: job.id,
      payment: 'create',
      create_client_id: job.client_id,
      create_job_id: job.id,
    })
    if (options.reportAfterPayment) params.set('report_job', job.id)
    if (options.invoiceId) params.set('create_invoice_id', options.invoiceId)
    if (amount != null) params.set('create_amount', String(amount))
    navigate(`/calendar?${params.toString()}`)
  }

  const startVisitQuoteCollectionStep = () => {
    setSearchParams(
      current => {
        const next = new URLSearchParams(current)
        next.set('collect', '1')
        if (isDrawer) next.set('job', job.id)
        next.delete('payment')
        next.delete('create_client_id')
        next.delete('create_job_id')
        next.delete('create_amount')
        next.delete('report_job')
        return next
      },
      { replace: false }
    )
  }

  const collectAmountForIntervention = () => {
    openAgendaPayment(job.amount_to_collect, {
      reportAfterPayment: job.status === JOB_STATUS.in_progress,
    })
  }

  const openPendingPayment = () => {
    if (!pendingPayment || !canViewPayment(pendingPayment.id)) return

    setSearchParams(
      current => {
        const next = new URLSearchParams(current)
        next.set('job', job.id)
        next.set('panel', 'detail')
        next.set('payment', pendingPayment.id)
        next.delete('detail_status')
        next.delete('create_client_id')
        next.delete('create_job_id')
        next.delete('create_invoice_id')
        next.delete('create_amount')
        next.delete('report_job')
        next.delete('collect')
        return next
      },
      { replace: false }
    )
  }

  const collectRemainingPaymentAmount = () => {
    if (remainingPaymentAmount <= 0) return

    openAgendaPayment(remainingPaymentAmount, {
      reportAfterPayment: job.status === JOB_STATUS.in_progress,
      invoiceId: job.invoice?.id,
    })
  }

  const collectRemainingPayment = canOpenPendingPayment
    ? openPendingPayment
    : collectRemainingPaymentAmount

  const collectVisitQuote = () => {
    if (!wonVisitQuote || dynamicStopPath.path !== 'collect') return

    openAgendaPayment(dynamicStopPath.amount, { reportAfterPayment: true })
  }

  const collectActionLabel = (amount: number | null | undefined) =>
    amount != null
      ? `${t('detail.toCollect')} ${formatCurrency(amount, 'EUR', 2)}`
      : t('detail.toCollect')

  const isVisitQuoteCollectionStep =
    (searchParams.get('collect') === '1' ||
      job.status === JOB_STATUS.in_progress) &&
    job.intervention_type === INTERVENTION_TYPE.visit_quote &&
    Boolean(wonVisitQuote) &&
    !hasLinkedPayment

  // Status-driven primary CTA. Create vs Edit report is driven by whether a
  // report EXISTS (any status), and the Create CTA is held back until that is
  // known (see reportExists) to avoid flashing "Create" then flipping to "Edit".
  const primaryAction: JobPrimaryAction | null = isToPlan
    ? {
        label: t('detail.startIntervention'),
        icon: <Play className='mr-2 h-4 w-4' />,
        onClick: openEditJobForm,
      }
    : job.intervention_type === INTERVENTION_TYPE.visit_quote &&
        (job.status === JOB_STATUS.planned ||
          job.status === JOB_STATUS.assigned)
      ? wonVisitQuote
        ? {
            label: t('detail.startIntervention'),
            icon: <Play className='mr-2 h-4 w-4' />,
            onClick: startIntervention,
            disabled: startJob.isPending,
          }
        : hasVisitQuote
          ? createOrEditReportAction
          : {
              label: t('detail.createQuote'),
              icon: <FilePlus className='mr-2 h-4 w-4' />,
              onClick: createQuoteForVisit,
            }
      : job.status === JOB_STATUS.planned || job.status === JOB_STATUS.assigned
        ? {
            label: t('detail.startIntervention'),
            icon: <Play className='mr-2 h-4 w-4' />,
            onClick: startIntervention,
            disabled: startJob.isPending,
          }
        : job.intervention_type === INTERVENTION_TYPE.visit_quote &&
            job.status === JOB_STATUS.in_progress
          ? wonVisitQuote
            ? dynamicStopPath.path === 'report'
              ? createOrEditReportAction
              : hasBillingPaid
                ? createOrEditReportAction
                : hasPartiallyPaidPayment
                  ? {
                      label: collectActionLabel(remainingPaymentAmount),
                      icon: <Euro className='mr-2 h-4 w-4' />,
                      onClick: collectRemainingPayment,
                    }
                  : canOpenPendingPayment
                    ? {
                        label: collectActionLabel(paymentTargetAmount),
                        icon: <Euro className='mr-2 h-4 w-4' />,
                        onClick: openPendingPayment,
                      }
                    : hasLinkedPayment
                      ? createOrEditReportAction
                      : isVisitQuoteCollectionStep
                        ? {
                            label: collectActionLabel(dynamicStopPath.amount),
                            icon: <Euro className='mr-2 h-4 w-4' />,
                            onClick: collectVisitQuote,
                          }
                        : {
                            label: t('detail.startIntervention'),
                            icon: <Play className='mr-2 h-4 w-4' />,
                            onClick: startVisitQuoteCollectionStep,
                          }
            : hasVisitQuote
              ? createOrEditReportAction
              : {
                  label: t('detail.createQuote'),
                  icon: <FilePlus className='mr-2 h-4 w-4' />,
                  onClick: createQuoteForVisit,
                }
          : isWarrantyReturnJob && job.status === JOB_STATUS.in_progress
            ? warrantyReturnPrimaryActionPath === 'collect' &&
              warrantyReturnStopPath?.path === 'collect'
              ? {
                  label: collectActionLabel(warrantyReturnStopPath.amount),
                  icon: <Euro className='mr-2 h-4 w-4' />,
                  onClick: () =>
                    openAgendaPayment(warrantyReturnStopPath.amount, {
                      reportAfterPayment: true,
                      invoiceId: warrantyReturnStopPath.invoiceId,
                    }),
                }
              : createOrEditReportAction
            : amountCollectableIntervention &&
                job.status === JOB_STATUS.in_progress
              ? hasBillingPaid
                ? createOrEditReportAction
                : hasPartiallyPaidPayment
                  ? {
                      label: collectActionLabel(remainingPaymentAmount),
                      icon: <Euro className='mr-2 h-4 w-4' />,
                      onClick: collectRemainingPayment,
                    }
                  : canOpenPendingPayment
                    ? {
                        label: collectActionLabel(paymentTargetAmount),
                        icon: <Euro className='mr-2 h-4 w-4' />,
                        onClick: openPendingPayment,
                      }
                    : hasLinkedPayment || !hasCollectableAmount
                      ? createOrEditReportAction
                      : {
                          label: collectActionLabel(job.amount_to_collect),
                          icon: <Euro className='mr-2 h-4 w-4' />,
                          onClick: collectAmountForIntervention,
                        }
              : job.status === JOB_STATUS.in_progress
                ? fieldWorkClosed
                  ? createOrEditReportAction
                  : {
                      label: t('detail.closeIntervention'),
                      icon: <CheckCircle2 className='mr-2 h-4 w-4' />,
                      onClick: () => setDialog('close'),
                    }
                : completed
                  ? createOrEditReportAction
                  : null
  const crAccentClass = isToPlan
    ? 'border-l-[#efac00]'
    : completed
      ? 'border-l-[#10b981]'
      : 'border-l-primary'
  const showCrActionBar =
    isCrExecutionJob && (!completed || reportExists === false)
  const showCrCompletedReportPanel =
    isCrExecutionJob && completed && reportExists === true
  const hasCompletedReport = report?.status === 'completed'
  const crTabClass =
    'border-transparent px-3 pb-2 pt-1 text-sm font-normal leading-[1.4] text-[#6c7285] data-[state=active]:border-[#ff8656] data-[state=active]:font-medium data-[state=active]:text-[#171a20]'
  const crTabsContentClass =
    'mt-0 min-h-0 flex-1 overflow-y-auto bg-[#f5f6fa] py-6 pl-6 pr-[7px]'
  const crPanelClass = 'mx-auto w-full max-w-[592px]'
  const crBillingAction =
    job.intervention_type === INTERVENTION_TYPE.visit_quote
      ? null
      : hasBillingPaid
        ? null
        : hasPartiallyPaidPayment
          ? {
              label: t('detail.collectNow'),
              onClick: collectRemainingPayment,
            }
          : canOpenPendingPayment
            ? {
                label: t('detail.collectNow'),
                onClick: openPendingPayment,
              }
            : hasLinkedPayment
              ? createOrEditReportAction
              : hasCollectableAmount
                ? {
                    label: t('detail.collectNow'),
                    onClick: collectAmountForIntervention,
                  }
                : null
  const visitQuoteBillingAmount = wonVisitQuote?.total ?? 0
  const hasVisitQuoteBillingAmount = visitQuoteBillingAmount > 0
  const paidDisplayAmount =
    paidPaymentAmount > 0 ? paidPaymentAmount : billingAmount
  const paidVisitQuoteDisplayAmount =
    paidPaymentAmount > 0 ? paidPaymentAmount : visitQuoteBillingAmount
  const visitQuoteBillingStatus = wonVisitQuote
    ? t('detail.cr.quoteApproved')
    : linkedVisitQuote
      ? t('detail.cr.quoteCreated')
      : t('detail.cr.quoteToBePrepared')
  const showVisitQuoteCollectionRow =
    job.intervention_type === INTERVENTION_TYPE.visit_quote &&
    Boolean(wonVisitQuote) &&
    (hasVisitQuoteBillingAmount || hasLinkedPayment || hasPartiallyPaidPayment)
  const visitQuoteBillingAction = hasBillingPaid
    ? null
    : hasPartiallyPaidPayment
      ? {
          label: t('detail.collectNow'),
          icon: undefined,
          onClick: collectRemainingPayment,
        }
      : canOpenPendingPayment
        ? {
            label: t('detail.collectNow'),
            icon: undefined,
            onClick: openPendingPayment,
          }
        : hasLinkedPayment
          ? createOrEditReportAction
          : hasVisitQuoteBillingAmount
            ? {
                label: t('detail.collectNow'),
                icon: undefined,
                onClick: collectVisitQuote,
              }
            : null

  const crSummaryContent = (
    <div
      className={cn(
        crPanelClass,
        'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'
      )}
    >
      <section>
        <CrSectionHeading title={t('detail.cr.detailsOfIntervention')} />
        <div className='space-y-4 border-b border-[#e7e9ef] px-6 pb-6 pt-4'>
          <div className='flex items-start justify-between gap-3'>
            <div className='min-w-0'>
              <div className='font-heading text-xl font-semibold leading-[1.2] text-[#171a20]'>
                {isToPlan ? t('detail.cr.notPlanned') : timeSlot}
              </div>
              <div className='mt-1 text-sm font-normal leading-[1.4] text-[#6c7285]'>
                {isToPlan ? t('detail.cr.notPlannedScheduleHint') : crDateLabel}
              </div>
            </div>
            <div className='flex min-w-0 flex-1 flex-nowrap items-start justify-end gap-1'>
              <JobStatusBadge
                status={job.status}
                className='shrink-0 font-normal'
              />
              {isWarrantyReturnJob ? (
                <CrWarrantyReturnBadge />
              ) : isUnderContractJob ? (
                <CrUnderContractBadge />
              ) : interventionTypeBadge ? (
                job.intervention_type ===
                INTERVENTION_TYPE.one_time_treatment ? (
                  <CrOneTimeTreatmentBadge>
                    {interventionTypeBadge}
                  </CrOneTimeTreatmentBadge>
                ) : (
                  <CrLightBadge>{interventionTypeBadge}</CrLightBadge>
                )
              ) : null}
              <JobPriorityBadge
                priority={job.priority}
                className='shrink-0 font-normal'
              />
            </div>
          </div>

          <div className='space-y-2'>
            {reportQuoteId && (
              <div className='flex h-11 items-center gap-2 overflow-hidden rounded-xl border border-[#e7e9ef] bg-[#f5f6fa] p-3'>
                <FileText className='size-4 shrink-0 text-[#6c7285]' />
                <span className='min-w-0 flex-1 truncate text-sm font-normal leading-[1.4] text-[#171a20]'>
                  {reportQuote?.number ?? t('detail.linkedQuoteDraft')}
                </span>
                <button
                  type='button'
                  className='inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-xs font-medium leading-none text-[#ff5a1f] disabled:cursor-not-allowed disabled:opacity-50'
                  disabled={!canViewQuote(reportQuoteId)}
                  onClick={() => {
                    if (canViewQuote(reportQuoteId))
                      navigate(`/quotes/${reportQuoteId}`)
                  }}
                >
                  {t('detail.cr.quoteDetails')}
                  <img src={arrowRightIcon} alt='' className='size-4' />
                </button>
              </div>
            )}
            <div className='flex h-11 items-center gap-2 overflow-hidden rounded-xl border border-[#e7e9ef] bg-[#f5f6fa] p-3'>
              <img src={mapPinIcon} alt='' className='size-4 shrink-0' />
              <span className='min-w-0 flex-1 truncate text-sm font-normal leading-[1.4] text-[#171a20]'>
                {fullAddress || '-'}
              </span>
              <button
                type='button'
                className='inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-xs font-medium leading-none text-[#ff5a1f]'
                onClick={() =>
                  navigateToAddress({
                    address: fullAddress,
                    latitude: job.latitude,
                    longitude: job.longitude,
                  })
                }
              >
                {t('detail.route')}
                <img src={arrowRightIcon} alt='' className='size-4' />
              </button>
            </div>
            <div className='flex h-11 items-center gap-2 overflow-hidden rounded-xl border border-[#e7e9ef] bg-[#f5f6fa] p-3'>
              <img src={userIcon} alt='' className='size-4 shrink-0' />
              <span className='min-w-0 flex-1 truncate text-sm font-normal leading-[1.4] text-[#171a20]'>
                {job.client?.name ?? '-'}
              </span>
              <button
                type='button'
                className='inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-xs font-medium leading-none text-[#ff5a1f] disabled:cursor-not-allowed disabled:opacity-50'
                disabled={!canViewClient}
                onClick={() => {
                  if (canViewClient) navigate(`/clients/${job.client_id}`)
                }}
              >
                {t('detail.profile')}
                <img src={arrowRightIcon} alt='' className='size-4' />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className='flex h-12 items-center gap-2 pr-6 pt-4'>
          <div className='flex min-w-0 flex-1 items-center overflow-hidden whitespace-nowrap border-l-4 border-[#ff5a1f] bg-white px-6 font-heading text-sm font-semibold uppercase leading-[1.2] text-[#6c7285]'>
            {t('detail.cr.noteForTechnician')}
          </div>
          {displayNote ? (
            <div className='flex items-center gap-2'>
              <Permit module='agenda' action='edit'>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-8 w-8 rounded-lg border border-[#e7e9ef] bg-white p-1.5'
                  onClick={() => setTab('notes')}
                  aria-label={t('detail.edit')}
                >
                  <img src={noteEditIcon} alt='' className='size-4' />
                </Button>
              </Permit>
              <Permit module='agenda' action='edit'>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-8 w-8 rounded-lg border border-[#e7e9ef] bg-white p-1.5'
                  onClick={clearNote}
                  aria-label={t('detail.delete')}
                >
                  <img src={noteTrashIcon} alt='' className='size-4' />
                </Button>
              </Permit>
            </div>
          ) : (
            <Permit module='agenda' action='edit'>
              <Button
                variant='ghost'
                size='sm'
                className='h-7 gap-1 px-2 text-xs font-medium text-[#ff5a1f] hover:text-[#ff5a1f]'
                onClick={() => setTab('notes')}
              >
                <Plus className='size-4' />
                {t('detail.cr.addNote')}
              </Button>
            </Permit>
          )}
        </div>
        <div className='border-b border-[#e7e9ef] px-6 pb-6 pt-4'>
          <div
            className={cn(
              'rounded-xl border px-3 py-3 text-sm leading-[1.4]',
              displayNote
                ? 'border-[#fde68a] bg-[#fbf3d9] font-medium text-[#171a20]'
                : 'border-[#e7e9ef] bg-[#f5f6fa] font-normal text-[#3d434e]'
            )}
          >
            {displayNote || t('detail.cr.noNotesYet')}
          </div>
        </div>
      </section>

      <section>
        <CrSectionHeading
          title={t('detail.cr.technician')}
          action={
            job.status !== JOB_STATUS.completed &&
            job.status !== JOB_STATUS.cancelled ? (
              <Permit module='agenda' action='edit'>
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-7 gap-1 px-2 text-xs font-medium text-[#ff5a1f] hover:text-[#ff5a1f]'
                  onClick={() => setDialog('reassign')}
                >
                  <UserPlus className='h-3.5 w-3.5' />
                  {t('detail.reassign')}
                </Button>
              </Permit>
            ) : undefined
          }
        />
        <div className='border-b border-[#e7e9ef] px-6 pb-6 pt-4'>
          {(job.assigned_technicians ?? []).length === 0 ? (
            <div className='rounded-xl border border-[#e7e9ef] bg-[#f5f6fa] px-3 py-3 text-sm font-normal leading-[1.4] text-[#3d434e]'>
              {isToPlan
                ? t('detail.cr.notYetAffected')
                : t('detail.noTechniciansAssigned')}
            </div>
          ) : (
            <div className='space-y-3'>
              {job.assigned_technicians.map((tech, i) => (
                <div key={tech.id} className='flex items-center gap-3'>
                  <Avatar
                    name={tech.full_name}
                    src={tech.avatar_url}
                    className='h-10 w-10 text-base text-white'
                    style={{
                      backgroundColor: AVATAR_COLORS[i % AVATAR_COLORS.length],
                    }}
                  />
                  <div className='min-w-0'>
                    <div className='truncate text-sm font-medium leading-[1.4] text-[#171a20]'>
                      {tech.full_name}
                      {tech.id === job.lead_technician_id &&
                        ` (${t('detail.lead')})`}
                    </div>
                    <div className='text-sm leading-[1.4] text-[#3d434e]'>
                      {i18n.t(
                        `technicians:form.rolePresets.${getTechnicianRolePreset(tech)}.title`
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section>
        <CrSectionHeading title={t('detail.cr.billing')} />
        <div className='px-6 pb-6 pt-4'>
          {job.intervention_type === INTERVENTION_TYPE.visit_quote ? (
            <div className='space-y-2'>
              <div className='flex h-[67px] items-center justify-between gap-3 rounded-xl border border-[#e7e9ef] bg-[#f5f6fa] px-4 py-3'>
                <div className='min-w-0'>
                  <div className='text-sm font-normal leading-[1.4] text-[#6c7285]'>
                    {t('detail.cr.onSiteCostEstimate')}
                  </div>
                  <div className='mt-0.5 truncate font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
                    {visitQuoteBillingStatus}
                  </div>
                </div>
                {linkedVisitQuote ? (
                  <Button
                    className='h-10 shrink-0 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 text-sm font-medium leading-none text-white hover:bg-[#e6501c]'
                    onClick={() => navigate(`/quotes/${linkedVisitQuote.id}`)}
                  >
                    <FilePlus className='mr-2 h-4 w-4' />
                    {t('detail.cr.quoteDetails')}
                  </Button>
                ) : (
                  <Permit module='agenda' action='edit'>
                    <Button
                      className='h-10 shrink-0 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 text-sm font-medium leading-none text-white hover:bg-[#e6501c]'
                      onClick={createQuoteForVisit}
                    >
                      <FilePlus className='mr-2 h-4 w-4' />
                      {t('detail.createQuote')}
                    </Button>
                  </Permit>
                )}
              </div>

              {showVisitQuoteCollectionRow && (
                <CrBillingPaymentRow
                  label={
                    hasBillingPaid
                      ? t('detail.cr.billing')
                      : hasPartiallyPaidPayment
                        ? t('detail.cr.remainingAmountToBeCollected')
                        : t('detail.cr.balanceToBeCollected')
                  }
                  amount={
                    hasBillingPaid
                      ? paidVisitQuoteDisplayAmount
                      : hasPartiallyPaidPayment
                        ? remainingPaymentAmount
                        : paymentTargetAmount
                  }
                  paid={hasBillingPaid}
                  paidLabel={t('detail.cr.paid')}
                  statusBadge={billingStatusBadge}
                  action={visitQuoteBillingAction}
                />
              )}
            </div>
          ) : warrantyReturnNothingToPay ? (
            <CrNoCollectionBillingCard
              text={t('detail.cr.warrantyReturnNothingToPay')}
            />
          ) : underContractNothingToPay ? (
            <CrNoCollectionBillingCard
              text={t('detail.cr.underContractIncluded')}
            />
          ) : (
            <CrBillingPaymentRow
              label={
                hasBillingPaid
                  ? t('detail.cr.billing')
                  : hasPartiallyPaidPayment
                    ? t('detail.cr.remainingAmountToBeCollected')
                    : t('detail.cr.balanceToBeCollected')
              }
              amount={
                hasBillingPaid
                  ? paidDisplayAmount
                  : hasPartiallyPaidPayment
                    ? remainingPaymentAmount
                    : paymentTargetAmount
              }
              paid={hasBillingPaid}
              paidLabel={t('detail.cr.paid')}
              statusBadge={billingStatusBadge}
              action={crBillingAction}
            />
          )}
        </div>
      </section>
    </div>
  )

  const crCompletedReportPanel = showCrCompletedReportPanel ? (
    <div className='rounded-2xl border border-[#86efac] bg-[#ecfdf5] p-4'>
      <div className='flex flex-col gap-4'>
        <div className='flex items-center gap-2 text-[#047857]'>
          <span className='flex size-[22px] shrink-0 items-center justify-center rounded-full bg-[#10b981] text-white'>
            <Check className='h-3.5 w-3.5' strokeWidth={1.6} />
          </span>
          <p className='font-sans text-sm font-normal leading-[1.4]'>
            {t('detail.cr.completedReportMessage')}
          </p>
        </div>
        <div className='grid grid-cols-[minmax(0,1fr)_auto_auto] gap-3'>
          <Permit module='agenda' action='edit'>
            <Button
              className='h-12 min-w-0 rounded-xl bg-[#ff5a1f] px-4 font-sans text-sm font-medium leading-none text-white hover:bg-[#ef4f16]'
              onClick={() => setDialog('send')}
            >
              <Send className='mr-2 h-4 w-4' strokeWidth={1.6} />
              {t('detail.cr.sentToClient')}
            </Button>
          </Permit>
          <Button
            variant='outline'
            className='h-12 rounded-xl border-[#e7e9ef] bg-white px-4 font-sans text-sm font-medium leading-none text-[#171a20] hover:bg-white hover:text-[#171a20]'
            onClick={exportReport}
          >
            <Download className='mr-2 h-4 w-4' strokeWidth={1.6} />
            {t('detail.cr.downloadPdf')}
          </Button>
          <Permit module='agenda' action='edit'>
            <Button
              variant='outline'
              className='h-12 rounded-xl border-[#e7e9ef] bg-white px-4 font-sans text-sm font-medium leading-none text-[#171a20] hover:bg-white hover:text-[#171a20]'
              onClick={() =>
                navigate(`/calendar?job=${id}&tab=reports&report=edit`)
              }
            >
              <Edit className='mr-2 h-4 w-4' strokeWidth={1.6} />
              {t('detail.edit')}
            </Button>
          </Permit>
        </div>
      </div>
    </div>
  ) : null

  return (
    <div
      className={cn(
        isCrExecutionJob && 'flex h-full min-h-0 flex-col bg-[#f5f6fa]',
        isCrExecutionJob && !isDrawer && '-m-6',
        !isCrExecutionJob && 'space-y-6'
      )}
    >
      <header
        className={cn(
          'space-y-4',
          isCrExecutionJob &&
            'shrink-0 border-l-[6px] bg-white pb-0 pl-[18px] pr-6 pt-6 shadow-[0_1px_4px_0_rgba(38,45,66,0.05),0_1px_4px_0_rgba(38,45,66,0.1)]',
          isCrExecutionJob && crAccentClass
        )}
      >
        {isCrExecutionJob ? (
          <>
            <div className='flex items-start justify-between gap-3'>
              <div className='flex min-w-0 items-center gap-3'>
                {!isDrawer && (
                  <Button
                    variant='ghost'
                    size='icon'
                    className='-ml-2 shrink-0'
                    onClick={() => navigate('/calendar')}
                    aria-label={t('detail.back')}
                  >
                    <ChevronLeft className='h-5 w-5' />
                  </Button>
                )}
                <h1 className='min-w-0 truncate font-heading text-xl font-semibold leading-[1.2] text-[#171a20]'>
                  {crHeaderTitle}
                </h1>
              </div>
              {isDrawer && (
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-10 w-10 shrink-0 rounded-xl bg-[#f5f6fa] text-[#272c36] hover:bg-[#eceef3]'
                  onClick={() => onClose?.()}
                  aria-label={t('detail.close')}
                >
                  <X className='h-5 w-5' />
                </Button>
              )}
            </div>

            <div className='flex flex-wrap items-center gap-3'>
              <TruncatedTextTooltip
                alwaysShow
                closeOnPointerDown
                className='inline-flex shrink-0'
                content={
                  job.client?.name
                    ? t('detail.callClient', { name: job.client.name })
                    : t('detail.call')
                }
                delayDuration={0}
              >
                <Button
                  variant='outline'
                  className='group h-[41px] rounded-full border-[#e7e9ef] bg-white py-1 pl-1 pr-3 text-sm font-medium text-[#171a20] shadow-[0_1px_4px_0_rgba(38,45,66,0.05),0_1px_4px_0_rgba(38,45,66,0.1)] hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'
                  onClick={() => callClient(primaryPhone(job.client))}
                >
                  <span className='mr-2 flex size-8 items-center justify-center rounded-full bg-[#ebf1fe] group-hover:bg-[#fff1ea]'>
                    <Phone className='h-4 w-4 text-[#2550cc] group-hover:text-[#ff5a1f]' />
                  </span>
                  {t('detail.toCall')}
                </Button>
              </TruncatedTextTooltip>
              <TruncatedTextTooltip
                alwaysShow
                closeOnPointerDown
                className='inline-flex shrink-0'
                content={t('detail.whatsapp')}
                delayDuration={0}
              >
                <Button
                  variant='outline'
                  className='h-[41px] rounded-full border-[#e7e9ef] bg-white py-1 pl-1 pr-3 text-sm font-medium text-[#171a20] shadow-[0_1px_4px_0_rgba(38,45,66,0.05),0_1px_4px_0_rgba(38,45,66,0.1)] hover:border-[#10b981] hover:bg-[#ecfdf5] hover:text-[#009951]'
                  onClick={() => whatsappClient(primaryPhone(job.client))}
                >
                  <span className='mr-2 flex size-8 items-center justify-center rounded-full bg-[#ecfdf5]'>
                    <WhatsAppIcon className='h-4 w-4 text-[#009951]' />
                  </span>
                  {t('detail.whatsapp')}
                </Button>
              </TruncatedTextTooltip>
              <TruncatedTextTooltip
                alwaysShow
                closeOnPointerDown
                className='inline-flex shrink-0'
                content={t('detail.itinerary')}
                delayDuration={0}
              >
                <Button
                  variant='outline'
                  className='h-[41px] rounded-full border-[#e7e9ef] bg-white py-1 pl-1 pr-3 text-sm font-medium text-[#171a20] shadow-[0_1px_4px_0_rgba(38,45,66,0.05),0_1px_4px_0_rgba(38,45,66,0.1)] hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'
                  onClick={() =>
                    navigateToAddress({
                      address: fullAddress,
                      latitude: job.latitude,
                      longitude: job.longitude,
                    })
                  }
                >
                  <span className='mr-2 flex size-8 items-center justify-center rounded-full bg-[#fff1ea]'>
                    <Navigation className='h-4 w-4 text-[#ff5a1f]' />
                  </span>
                  {t('detail.itinerary')}
                </Button>
              </TruncatedTextTooltip>
            </div>

            {crCompletedReportPanel}

            {showCrActionBar && (
              <div className='flex h-[72px] items-center gap-3 rounded-2xl border border-[#e7e9ef] bg-[#f5f6fa] px-4 py-3'>
                {primaryAction && (
                  <Permit module='agenda' action='edit'>
                    <Button
                      className='h-12 flex-1 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 py-4 text-sm font-medium leading-none text-white hover:bg-[#e6501c]'
                      onClick={primaryAction.onClick}
                      disabled={primaryAction.disabled}
                    >
                      <span className='mr-2 flex size-[30px] items-center justify-center rounded-full bg-white/30 [&_svg]:m-0 [&_svg]:h-4 [&_svg]:w-4'>
                        {primaryAction.icon}
                      </span>
                      {primaryAction.label}
                    </Button>
                  </Permit>
                )}
                {showDuplicate && (
                  <Permit module='agenda' action='edit'>
                    <Button
                      className='h-12 flex-1 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 py-4 text-sm font-medium leading-none text-white hover:bg-[#e6501c]'
                      onClick={() =>
                        navigate(`/jobs/new?duplicate_from=${job.id}`)
                      }
                    >
                      <span className='mr-2 flex size-[30px] items-center justify-center rounded-full bg-white/30'>
                        <Play className='size-4' />
                      </span>
                      {t('detail.duplicateToNewDate')}
                    </Button>
                  </Permit>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant='outline'
                      className='h-12 shrink-0 rounded-xl border-[#e7e9ef] bg-white px-4 text-sm font-medium leading-none text-[#171a20] hover:bg-white hover:text-[#171a20]'
                    >
                      {t('detail.actions')}
                      <ChevronDown className='ml-2 h-4 w-4' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align='end'
                    sideOffset={8}
                    className='w-[320px] max-w-[calc(100vw-32px)] rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'
                  >
                    {showEdit && (
                      <DropdownMenuItem
                        className='min-h-12 gap-3 rounded-xl p-2 text-sm font-normal leading-[1.4] text-[#171a20] focus:bg-[#f5f6fa] focus:text-[#171a20]'
                        onClick={openEditJobForm}
                      >
                        <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f5f6fa]'>
                          <PencilLine className='size-4' />
                        </span>
                        <span className='min-w-0 flex-1 whitespace-normal'>
                          {t('detail.actionMenu.modify')}
                        </span>
                      </DropdownMenuItem>
                    )}
                    {job.status === JOB_STATUS.in_progress && (
                      <DropdownMenuItem
                        className='min-h-12 gap-3 rounded-xl p-2 text-sm font-normal leading-[1.4] text-[#171a20] focus:bg-[#f5f6fa] focus:text-[#171a20]'
                        onClick={undoStart}
                      >
                        <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f5f6fa]'>
                          <Ban className='size-4' />
                        </span>
                        <span className='min-w-0 flex-1 whitespace-normal'>
                          {t('detail.cancelStartup')}
                        </span>
                      </DropdownMenuItem>
                    )}
                    {showReschedule && (
                      <DropdownMenuItem
                        className='min-h-12 gap-3 rounded-xl p-2 text-sm font-normal leading-[1.4] text-[#171a20] focus:bg-[#f5f6fa] focus:text-[#171a20]'
                        onClick={openRescheduleJobForm}
                      >
                        <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f5f6fa]'>
                          <UserX className='size-4' />
                        </span>
                        <span className='min-w-0 flex-1 whitespace-normal'>
                          {t('detail.actionMenu.clientUnreachable')}
                        </span>
                      </DropdownMenuItem>
                    )}
                    {showCancel && (
                      <DropdownMenuItem
                        className='min-h-12 gap-3 rounded-xl p-2 text-sm font-normal leading-[1.4] text-[#171a20] focus:bg-[#f5f6fa] focus:text-[#171a20]'
                        onClick={() => setDialog('cancel')}
                      >
                        <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f5f6fa]'>
                          <X className='size-4' />
                        </span>
                        <span className='min-w-0 flex-1 whitespace-normal'>
                          {t('detail.actionMenu.clientCancels')}
                        </span>
                      </DropdownMenuItem>
                    )}
                    {(showDeleteIcon || showDeleteFull) && (
                      <>
                        {(showEdit ||
                          job.status === JOB_STATUS.in_progress ||
                          showReschedule ||
                          showCancel) && (
                          <DropdownMenuSeparator className='mx-2 my-2 bg-[#e7e9ef]' />
                        )}
                        <DropdownMenuItem
                          className='min-h-12 gap-3 rounded-xl p-2 text-sm font-normal leading-[1.4] text-[#d63b33] focus:bg-[#fde9e8]/50 focus:text-[#d63b33]'
                          onClick={() => setDialog('delete')}
                        >
                          <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#fde9e8]'>
                            <Trash2 className='size-4' />
                          </span>
                          <span className='min-w-0 flex-1 whitespace-normal'>
                            {t('detail.actionMenu.removeIntervention')}
                          </span>
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {isToPlan && (
              <div className='flex items-start gap-3 rounded-xl border border-[#fde68a] bg-[#fbf3d9] px-4 py-3'>
                <span className='mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-white text-[#ad6800]'>
                  <CalendarClock className='size-4' />
                </span>
                <div className='min-w-0'>
                  <p className='text-sm font-semibold leading-[1.4] text-[#171a20]'>
                    {t('detail.cr.notPlannedMessageTitle')}
                  </p>
                  <p className='mt-1 text-xs font-normal leading-4 text-[#3d434e]'>
                    {callbackDescription ?? t('detail.cr.notPlannedMessage')}
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Top row: status & priority badges lead; the drawer closes via a
                top-right X, the standalone page goes back via a leading chevron. */}
            <div className='flex items-center justify-between gap-3'>
              <div className='flex flex-wrap items-center gap-2'>
                {!isDrawer && (
                  <Button
                    variant='ghost'
                    size='icon'
                    className='-ml-2'
                    onClick={() => navigate('/calendar')}
                    aria-label={t('detail.back')}
                  >
                    <ChevronLeft className='h-5 w-5' />
                  </Button>
                )}
                <JobStatusBadge status={job.status} />
                <JobPriorityBadge priority={job.priority} />
              </div>
              {isDrawer && (
                <Button
                  variant='ghost'
                  size='icon'
                  className='-mr-2 shrink-0'
                  onClick={() => onClose?.()}
                  aria-label={t('detail.close')}
                >
                  <X className='h-5 w-5' />
                </Button>
              )}
            </div>

            {/* Job reference as the header title (Figma H1). */}
            <h1 className='text-2xl font-semibold tracking-tight'>
              {job.reference ??
                job.client?.name ??
                t('detail.jobFallback', { id: job.id.slice(0, 8) })}
            </h1>

            {/* Quick-action bar: WhatsApp (green) · Call · Itinerary, divided from
                the primary action group below. */}
            <div className='flex flex-wrap items-center gap-3 border-b border-border pb-4'>
              <Button
                variant='outline'
                className='border-[#009951] text-[#009951] hover:bg-[#009951]/10 hover:text-[#009951]'
                onClick={() => whatsappClient(primaryPhone(job.client))}
              >
                <WhatsAppIcon className='mr-2' /> {t('detail.whatsapp')}
              </Button>
              <Button
                variant='outline'
                onClick={() => callClient(primaryPhone(job.client))}
              >
                <Phone className='mr-2 h-4 w-4' /> {t('detail.call')}
              </Button>
              <Button
                variant='outline'
                onClick={() =>
                  navigateToAddress({
                    address: fullAddress,
                    latitude: job.latitude,
                    longitude: job.longitude,
                  })
                }
              >
                <Navigation className='mr-2 h-4 w-4' /> {t('detail.navigate')}
              </Button>
            </div>
          </>
        )}
      </header>

      {!isCrExecutionJob && (
        <div className='flex flex-wrap items-center gap-3'>
          {primaryAction && (
            <Permit module='agenda' action='edit'>
              <Button
                className='flex-1'
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled}
              >
                {primaryAction.icon}
                {primaryAction.label}
              </Button>
            </Permit>
          )}
          {showEdit && (
            <Permit module='agenda' action='edit'>
              <Button variant='outline' onClick={openEditJobForm}>
                <Pencil className='mr-2 h-4 w-4' /> {t('detail.edit')}
              </Button>
            </Permit>
          )}
          {job.status === JOB_STATUS.in_progress && (
            <Permit module='agenda' action='edit'>
              <Button
                variant='outline'
                onClick={undoStart}
                disabled={cancelStartup.isPending}
              >
                <RotateCcw className='mr-2 h-4 w-4' />
                {t('detail.cancelStartup')}
              </Button>
            </Permit>
          )}
          {showReschedule && (
            <Permit module='agenda' action='edit'>
              <Button variant='outline' onClick={openRescheduleJobForm}>
                <CalendarClock className='mr-2 h-4 w-4' />
                {t('detail.reschedule')}
              </Button>
            </Permit>
          )}
          {showCancel && (
            <Permit module='agenda' action='edit'>
              <Button variant='outline' onClick={() => setDialog('cancel')}>
                <X className='mr-2 h-4 w-4' /> {t('detail.cancelJob')}
              </Button>
            </Permit>
          )}
          {showCreateInvoice && (
            <Permit module='invoices' action='edit'>
              <Button
                variant='outline'
                onClick={() => navigate(`/invoices/new?job_id=${job.id}`)}
              >
                <FilePlus className='mr-2 h-4 w-4' />
                {t('detail.createInvoice')}
              </Button>
            </Permit>
          )}
          {showDuplicate && (
            <Permit module='agenda' action='edit'>
              <Button
                variant='outline'
                className='flex-1'
                onClick={() => navigate(`/jobs/new?duplicate_from=${job.id}`)}
              >
                <CalendarPlus className='mr-2 h-4 w-4' />
                {t('detail.duplicateToNewDate')}
              </Button>
            </Permit>
          )}
          {showDeleteIcon && (
            <Permit module='agenda' action='delete'>
              <Button
                variant='outline'
                size='icon'
                className='shrink-0 border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive'
                onClick={() => setDialog('delete')}
                aria-label={t('detail.delete')}
              >
                <Trash2 className='h-4 w-4' />
              </Button>
            </Permit>
          )}
          {showDeleteFull && (
            <Permit module='agenda' action='delete'>
              <Button
                variant='destructive'
                className='flex-1'
                onClick={() => setDialog('delete')}
              >
                <Trash2 className='mr-2 h-4 w-4' /> {t('detail.delete')}
              </Button>
            </Permit>
          )}
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={setTab}
        className={
          isCrExecutionJob ? 'flex min-h-0 flex-1 flex-col' : undefined
        }
      >
        <TabsList
          className={cn(
            'w-full',
            isCrExecutionJob &&
              'mt-0 shrink-0 gap-0 overflow-x-auto border-b border-l-[6px] border-b-[#e7e9ef] bg-white pb-0 pl-[18px] pr-6 pt-6',
            isCrExecutionJob && crAccentClass
          )}
        >
          <TabsTrigger
            value='summary'
            className={isCrExecutionJob ? crTabClass : undefined}
          >
            {t('detail.tabs.summary')}
          </TabsTrigger>
          <TabsTrigger
            value='client'
            className={isCrExecutionJob ? crTabClass : undefined}
          >
            {t('detail.tabs.client')}
          </TabsTrigger>
          <TabsTrigger
            value='notes'
            className={isCrExecutionJob ? crTabClass : undefined}
          >
            {t('detail.tabs.notes')}
          </TabsTrigger>
          <TabsTrigger
            value='attachments'
            className={isCrExecutionJob ? crTabClass : undefined}
          >
            {t('detail.tabs.attachments')}
          </TabsTrigger>
          <TabsTrigger
            value='reports'
            className={isCrExecutionJob ? crTabClass : undefined}
          >
            <span className='inline-flex items-center gap-1.5'>
              {t('detail.tabs.reports')}
              {hasCompletedReport && (
                <span className='inline-flex h-6 w-[26px] items-center justify-center rounded-lg bg-[#d1fae5] text-[#065f46]'>
                  <Check className='size-3.5' strokeWidth={1.6} />
                </span>
              )}
            </span>
          </TabsTrigger>
          {!isDrawer && (
            <TabsTrigger
              value='history'
              className={isCrExecutionJob ? crTabClass : undefined}
            >
              {t('detail.tabs.history')}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent
          value='summary'
          className={isCrExecutionJob ? crTabsContentClass : undefined}
        >
          {isCrExecutionJob ? (
            crSummaryContent
          ) : (
            <div className='space-y-4'>
              {/* Intervention Details — brand-bar heading. */}
              <Card className='overflow-hidden'>
                <CardHeadingBar title={t('detail.interventionDetails')} />
                <CardContent className='divide-y divide-border pt-4'>
                  <Row
                    label={t('detail.fields.client')}
                    value={
                      job.client ? (
                        <span className='inline-flex min-w-0 items-center gap-1'>
                          {canViewClient ? (
                            <button
                              className='font-medium text-[#ff5a1f] hover:underline'
                              onClick={() =>
                                navigate(`/clients/${job.client_id}`)
                              }
                            >
                              {job.client.name}
                            </button>
                          ) : (
                            <span>{job.client.name}</span>
                          )}
                          <ProspectBadge category={job.client.category} />
                        </span>
                      ) : (
                        '-'
                      )
                    }
                  />
                  <Row
                    label={t('detail.fields.scheduledDate')}
                    value={`${formatDate(job.scheduled_date)}, ${timeSlot}`}
                  />
                  {/* Feedback #16 - service type on the detail view. */}
                  <Row
                    label={t('detail.fields.serviceType')}
                    value={job.service_type ?? '-'}
                  />
                  <Row
                    label={t('detail.fields.address')}
                    value={fullAddress || '-'}
                  />
                  <Row
                    label={t('detail.fields.priority')}
                    value={<JobPriorityBadge priority={job.priority} />}
                  />
                  <Row
                    label={t('detail.fields.status')}
                    value={<JobStatusBadge status={job.status} />}
                  />
                  {job.payment_option && (
                    <Row
                      label={t('detail.fields.payment')}
                      value={`${PAYMENT_OPTION_LABELS[job.payment_option]}${job.payment_option === PAYMENT_OPTION.to_collect && job.amount_to_collect ? ` · ${formatCurrency(job.amount_to_collect, 'EUR', 2)}` : ''}`}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Assigned Technicians — Reassign moves into the heading; each
                technician is an avatar block with initials/photo + name. */}
              <Card className='overflow-hidden'>
                <CardHeadingBar
                  title={t('detail.assignedTechnicians')}
                  action={
                    job.status !== JOB_STATUS.completed &&
                    job.status !== JOB_STATUS.cancelled ? (
                      <Permit module='agenda' action='edit'>
                        <Button
                          variant='ghost'
                          size='sm'
                          className='h-auto shrink-0 gap-1 px-2 text-[#ff5a1f] hover:text-[#ff5a1f]'
                          onClick={() => setDialog('reassign')}
                        >
                          <UserPlus className='h-4 w-4' />
                          {t('detail.reassign')}
                        </Button>
                      </Permit>
                    ) : undefined
                  }
                />
                <CardContent className='pt-4'>
                  {(job.assigned_technicians ?? []).length === 0 ? (
                    <p className='text-sm text-muted-foreground'>
                      {t('detail.noTechniciansAssigned')}
                    </p>
                  ) : (
                    <div className='divide-y divide-border'>
                      {job.assigned_technicians.map((tech, i) => (
                        <div
                          key={tech.id}
                          className='flex items-center gap-3 py-2'
                        >
                          <Avatar
                            name={tech.full_name}
                            src={tech.avatar_url}
                            className='h-8 w-8 text-white'
                            style={{
                              backgroundColor:
                                AVATAR_COLORS[i % AVATAR_COLORS.length],
                            }}
                          />
                          <span className='flex-1 text-sm font-semibold'>
                            {tech.full_name}
                          </span>
                          {tech.id === job.lead_technician_id && (
                            <Badge variant='info'>{t('detail.lead')}</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Feedback #17 / bug 2.3.6 - Chase Invoice section renders only
                when the linked invoice still has money owed — not for Draft /
                Sent / Paid invoices. */}
              {job.invoice &&
                UNPAID_INVOICE_STATUSES.includes(job.invoice.status) && (
                  <ChaseInvoiceCard invoice={job.invoice} />
                )}

              {/* 2.3.18 - post-inspection quotes raised from this job. The panel
                reflects the newly created quote after the Create quote flow. */}
              {job.quotes && job.quotes.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className='text-base'>
                      {t('detail.linkedQuotes')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-2'>
                    {job.quotes.map(quote => (
                      <button
                        key={quote.id}
                        type='button'
                        className='flex w-full items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-left hover:bg-accent'
                        onClick={() => {
                          if (canViewQuote(quote.id))
                            navigate(`/quotes/${quote.id}`)
                        }}
                        disabled={!canViewQuote(quote.id)}
                      >
                        <span className='flex items-center gap-2'>
                          <span className='text-sm font-medium'>
                            {quote.number ?? t('detail.linkedQuoteDraft')}
                          </span>
                          <QuoteStatusBadge status={quote.status} />
                        </span>
                        <span className='text-sm font-semibold'>
                          {formatCurrency(quote.total, 'EUR', 2)}
                        </span>
                      </button>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* 2.3.19 / BR11 - payments recorded against this job (portal or
                mobile), shown read-only so a collection is not duplicated. */}
              {job.payments && job.payments.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className='text-base'>
                      {t('detail.linkedPayments')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-2'>
                    {job.payments.map(payment => (
                      <button
                        key={payment.id}
                        type='button'
                        className='flex w-full items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-left hover:bg-accent'
                        onClick={() => {
                          if (canViewPayment(payment.id)) {
                            navigate(`/payments/${payment.id}`)
                          }
                        }}
                        disabled={!canViewPayment(payment.id)}
                      >
                        <span className='flex items-center gap-2'>
                          <span className='text-sm font-medium'>
                            {PAYMENT_METHOD_LABELS[payment.method]}
                          </span>
                          <PaymentStatusBadge status={payment.status} />
                        </span>
                        <span className='text-sm font-semibold'>
                          {formatCurrency(payment.amount, 'EUR', 2)}
                        </span>
                      </button>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent
          value='client'
          className={
            isCrExecutionJob ? crTabsContentClass : 'mt-0 bg-[#f5f6fa] p-6'
          }
        >
          <Card
            className={cn(
              isCrExecutionJob && crPanelClass,
              'overflow-hidden rounded-2xl border-[#e7e9ef] shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'
            )}
          >
            <CrSectionHeading title={t('form.clientInformation')} />
            <CardContent className='flex flex-col gap-4 px-6 pb-6 pt-4'>
              <div className='flex flex-col gap-2'>
                <ClientInformationRow
                  label={t('detail.clientFields.name')}
                  value={
                    job.client?.name && canViewClient ? (
                      <button
                        type='button'
                        className='min-w-0 truncate text-right text-[#ff5a1f] hover:underline'
                        onClick={() => navigate(`/clients/${job.client_id}`)}
                      >
                        {job.client.name}
                      </button>
                    ) : (
                      job.client?.name
                    )
                  }
                  highlight
                />
                <ClientInformationRow
                  label={t('detail.clientFields.phone')}
                  value={
                    clientPhone ? (
                      <button
                        type='button'
                        className='min-w-0 truncate text-[#ff5a1f] hover:underline'
                        onClick={() => callClient(clientPhone)}
                      >
                        {clientPhone}
                      </button>
                    ) : undefined
                  }
                  highlight
                />
                <ClientInformationRow
                  label={t('detail.clientFields.email')}
                  value={
                    clientEmail ? (
                      <button
                        type='button'
                        className='min-w-0 truncate text-[#ff5a1f] hover:underline'
                        onClick={() => emailClient(clientEmail)}
                      >
                        {clientEmail}
                      </button>
                    ) : undefined
                  }
                  highlight
                />
                <ClientInformationRow
                  label={t('detail.clientFields.address')}
                  value={fullAddress}
                />
                <ClientInformationRow
                  label={t('detail.clientFields.type')}
                  value={interventionTypeLabel}
                />
                <ClientInformationRow
                  label={t('detail.clientFields.reference')}
                  value={job.reference}
                />
              </div>
              <div>
                <Button
                  variant='outline'
                  className='h-10 rounded-xl px-3 text-sm font-medium leading-none'
                  onClick={() => {
                    if (canViewClient) navigate(`/clients/${job.client_id}`)
                  }}
                  disabled={!canViewClient}
                >
                  <User className='mr-2 size-4' />
                  {t('detail.viewFullClient')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent
          value='notes'
          className={
            isCrExecutionJob ? crTabsContentClass : 'mt-0 bg-[#f5f6fa] p-6'
          }
        >
          <Card
            className={cn(
              isCrExecutionJob && crPanelClass,
              'overflow-hidden rounded-2xl border-[#e7e9ef] shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'
            )}
          >
            <CrSectionHeading title={t('detail.internalNotes')} />
            <CardContent className='flex flex-col items-end gap-4 px-6 pb-6 pt-4'>
              <div className='flex w-full flex-col gap-1'>
                <Textarea
                  rows={4}
                  value={noteValue}
                  readOnly={!canEditAgenda}
                  onChange={e => setNoteDraft(e.target.value)}
                  placeholder={t('detail.notePlaceholder')}
                  className={cn(
                    'resize-y read-only:cursor-default',
                    FORM_TEXTAREA_CLASS,
                    'font-medium placeholder:font-normal'
                  )}
                />
                {job.note?.trim() ? (
                  <p className='text-sm font-normal leading-[1.4] text-[#3d434e]'>
                    {noteModificationLabel}
                  </p>
                ) : null}
              </div>
              <Permit module='agenda' action='edit'>
                <Button
                  className='h-10 rounded-xl bg-[#ff5a1f] px-3 text-sm font-medium leading-none text-white hover:bg-[#ef4f16]'
                  onClick={saveNote}
                  disabled={updateJob.isPending}
                >
                  {t('form.save')}
                </Button>
              </Permit>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent
          value='attachments'
          className={
            isCrExecutionJob ? crTabsContentClass : 'mt-0 bg-[#f5f6fa] p-6'
          }
        >
          <Card
            className={cn(
              isCrExecutionJob && crPanelClass,
              'overflow-hidden rounded-2xl border-[#e7e9ef] shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'
            )}
          >
            <CrSectionHeading title={t('detail.tabs.attachments')} />
            <CardContent className='px-6 pb-6 pt-4'>
              <JobAttachments key={id} jobId={id} clientId={job.client_id} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent
          value='reports'
          className={
            isCrExecutionJob ? crTabsContentClass : 'mt-0 bg-[#f5f6fa] p-6'
          }
        >
          {completed && reportQuery.isLoading ? (
            <Skeleton className='h-[235px] w-full rounded-2xl' />
          ) : !report ? (
            <Card
              className={cn(
                isCrExecutionJob && crPanelClass,
                'overflow-hidden rounded-2xl border-[#e7e9ef] shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'
              )}
            >
              <CrSectionHeading
                title={t('detail.reportsTitle')}
                action={
                  <Permit module='agenda' action='edit'>
                    <Button
                      variant='ghost'
                      className='h-8 rounded-lg p-2 text-xs font-medium leading-none text-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'
                      onClick={openCreateReportFromTab}
                    >
                      <FilePlus className='mr-2 size-4' />
                      {t('detail.createReport')}
                    </Button>
                  </Permit>
                }
              />
              <CardContent className='px-6 pb-6 pt-4'>
                <div className='flex min-h-[148px] w-full flex-col items-center justify-center gap-4 overflow-hidden rounded-xl bg-[#f5f6fa] p-6 text-center'>
                  <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#171a20]'>
                    <FileText className='size-5' />
                  </span>
                  <div className='flex flex-col items-center gap-1'>
                    <p className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
                      {t('detail.reportEmptyTitle')}
                    </p>
                    <p className='text-xs font-normal leading-4 text-[#6c7285]'>
                      {t('detail.reportEmptyDescription')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <JobReportView
              report={report}
              onDownload={exportReport}
              downloading={exportingReport}
              onSend={canEditAgenda ? () => setDialog('send') : undefined}
            />
          )}
        </TabsContent>
        {!isDrawer && (
          <TabsContent value='history'>
            <Card>
              <CardContent className='divide-y divide-border pt-6'>
                {(job.status_history ?? []).length === 0 ? (
                  <p className='py-6 text-center text-sm text-muted-foreground'>
                    {t('detail.history.empty')}
                  </p>
                ) : (
                  job.status_history?.map(item => (
                    <div key={item.id} className='space-y-1 py-3 text-sm'>
                      <div className='flex items-start justify-between gap-4'>
                        <span className='font-medium'>
                          {item.description ?? item.event_type}
                        </span>
                        <span className='shrink-0 text-xs text-muted-foreground'>
                          {new Date(item.created_at).toLocaleString()}
                        </span>
                      </div>
                      {typeof item.properties?.reason === 'string' && (
                        <p className='text-muted-foreground'>
                          {t('detail.history.reason')}: {item.properties.reason}
                        </p>
                      )}
                      {item.user && (
                        <p className='text-xs text-muted-foreground'>
                          {t('detail.history.by', { user: item.user })}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <DeleteJobDialog
        job={job}
        open={dialog === 'delete'}
        onOpenChange={o => setDialog(o ? 'delete' : null)}
      />
      <CancelJobDialog
        job={job}
        open={dialog === 'cancel'}
        onOpenChange={o => setDialog(o ? 'cancel' : null)}
      />
      <StartCloseDialog
        job={job}
        mode='close'
        open={dialog === 'close'}
        onOpenChange={o => setDialog(o ? 'close' : null)}
      />
      {dialog === 'reassign' && (
        <ReassignDialog
          job={job}
          open
          onOpenChange={o => setDialog(o ? 'reassign' : null)}
        />
      )}
      {dialog === 'send' && (
        <SendReportDialog
          jobId={id}
          defaultEmail={primaryEmail(job.client) ?? ''}
          defaultBody={t('sendReport.defaultBody')}
          onClose={() => setDialog(null)}
        />
      )}
      <LoadingOverlay open={exportingReport} />
    </div>
  )
}
